// Extraction de texte côté client pour limiter la consommation de tokens API Claude.
// - PDF : pdfjs-dist, on ne prend que les N premières pages
// - Excel (xlsx/xls) : SheetJS en mode texte, une feuille = une section
// - Texte brut / markdown / csv : tel quel
// Retourne un string compact prêt à envoyer à l'API.

import * as XLSX from 'xlsx';

const MAX_TEXT_CHARS = 60_000;

export type ExtractInput = {
  filename: string;
  mime: string | null;
  fileUrl: string; // /api/v1/files?key=...
};

export type ExtractResult = {
  kind: 'pdf' | 'excel' | 'text' | 'image' | 'binary';
  text: string;
  pages?: number;
  sheets?: string[];
  truncated?: boolean;
};

function isPdf(m: string | null, name: string): boolean {
  return m === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
}
function isExcel(m: string | null, name: string): boolean {
  const n = name.toLowerCase();
  return (
    (m ?? '').includes('spreadsheet') ||
    (m ?? '').includes('excel') ||
    n.endsWith('.xlsx') ||
    n.endsWith('.xls') ||
    n.endsWith('.xlsm') ||
    n.endsWith('.xlsb')
  );
}
function isPlainText(m: string | null, name: string): boolean {
  const n = name.toLowerCase();
  return (
    (m ?? '').startsWith('text/') ||
    n.endsWith('.txt') ||
    n.endsWith('.md') ||
    n.endsWith('.csv') ||
    n.endsWith('.json')
  );
}

async function extractPdf(fileUrl: string, maxPages = 15): Promise<ExtractResult> {
  const pdfjs: any = await import('pdfjs-dist');
  try {
    // Worker pour Vite
    const workerMod: any = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjs.GlobalWorkerOptions.workerSrc = workerMod.default;
  } catch {
    /* fallback : pas de worker, pdfjs bascule sur main thread */
  }
  const res = await fetch(fileUrl, { credentials: 'include' });
  if (!res.ok) throw new Error(`Téléchargement du PDF impossible (HTTP ${res.status})`);
  const buf = await res.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data: buf });
  const pdf = await loadingTask.promise;
  const pages = pdf.numPages;
  const lim = Math.min(pages, maxPages);
  const parts: string[] = [];
  for (let p = 1; p <= lim; p++) {
    try {
      const page = await pdf.getPage(p);
      const content = await page.getTextContent();
      const text = content.items
        .map((it: any) => (typeof it.str === 'string' ? it.str : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (text) parts.push(`--- Page ${p} ---\n${text}`);
    } catch {
      /* skip */
    }
  }
  let text = parts.join('\n\n');
  const truncated = pages > lim || text.length > MAX_TEXT_CHARS;
  if (text.length > MAX_TEXT_CHARS) text = text.slice(0, MAX_TEXT_CHARS) + '\n\n[…tronqué]';
  if (pages > lim) text = `[PDF de ${pages} pages — seules les ${lim} premières sont analysées]\n\n` + text;
  return { kind: 'pdf', text, pages, truncated };
}

function sheetToMarkdown(ws: XLSX.WorkSheet): string {
  const arr: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
  if (arr.length === 0) return '(feuille vide)';
  // Trouve la largeur max
  const w = Math.max(...arr.map((r) => r.length));
  const lines: string[] = [];
  const header = arr[0];
  lines.push('| ' + header.map((c) => String(c ?? '').trim()).join(' | ') + ' |');
  lines.push('|' + Array.from({ length: w }, () => '---').join('|') + '|');
  const dataRows = arr.slice(1, 200); // limite à 200 lignes par feuille
  for (const r of dataRows) {
    const cells = Array.from({ length: w }, (_, i) => String(r[i] ?? '').trim());
    lines.push('| ' + cells.join(' | ') + ' |');
  }
  if (arr.length - 1 > 200) lines.push(`… (${arr.length - 201} lignes supplémentaires omises)`);
  return lines.join('\n');
}

async function extractExcel(fileUrl: string): Promise<ExtractResult> {
  const res = await fetch(fileUrl, { credentials: 'include' });
  if (!res.ok) throw new Error(`Téléchargement Excel impossible (HTTP ${res.status})`);
  const buf = await res.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheets = wb.SheetNames;
  const parts: string[] = [];
  for (const name of sheets.slice(0, 6)) {
    parts.push(`## Feuille : ${name}\n\n${sheetToMarkdown(wb.Sheets[name])}`);
  }
  let text = parts.join('\n\n');
  const truncated = text.length > MAX_TEXT_CHARS || sheets.length > 6;
  if (text.length > MAX_TEXT_CHARS) text = text.slice(0, MAX_TEXT_CHARS) + '\n\n[…tronqué]';
  if (sheets.length > 6) text += `\n\n[${sheets.length - 6} feuilles supplémentaires non lues]`;
  return { kind: 'excel', text, sheets, truncated };
}

async function extractText(fileUrl: string): Promise<ExtractResult> {
  const res = await fetch(fileUrl, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  let text = await res.text();
  const truncated = text.length > MAX_TEXT_CHARS;
  if (truncated) text = text.slice(0, MAX_TEXT_CHARS) + '\n\n[…tronqué]';
  return { kind: 'text', text, truncated };
}

export async function extractDocument(input: ExtractInput): Promise<ExtractResult> {
  const { filename, mime, fileUrl } = input;
  if (isPdf(mime, filename)) return extractPdf(fileUrl, 15);
  if (isExcel(mime, filename)) return extractExcel(fileUrl);
  if (isPlainText(mime, filename)) return extractText(fileUrl);
  if ((mime ?? '').startsWith('image/')) {
    return { kind: 'image', text: `[Image ${filename}, non extractible en texte.]` };
  }
  return { kind: 'binary', text: `[Fichier binaire ${filename} (${mime ?? 'inconnu'}), non lu.]` };
}
